import { t } from "../i18n/index.ts";
import {
  COVER_MAX_UPLOAD_BYTES,
  COVER_WEBP_QUALITY,
  coverFileProblem,
  coverScale,
} from "../../shared/quest-cover.ts";
import { type CoverCrop } from "../../shared/quest-cover.ts";
import {
  spotThumbCropOutput,
  spotThumbPanCrop,
  spotThumbStartCrop,
  spotThumbZoomCrop,
} from "../../shared/spot-thumb-crop.ts";
import { SPOT_THUMB_OUTPUT_PX } from "../../shared/spot-thumbnail.ts";
import { button, el } from "./dom.ts";

export class SpotThumbPickError extends Error {}

const KEY_PAN_PX = 12;
const WHEEL_ZOOM = 0.0018;

/** Pick → square crop → encode at {@link SPOT_THUMB_OUTPUT_PX}×{@link SPOT_THUMB_OUTPUT_PX}. */
export async function cropSpotThumbnail(file: File): Promise<Blob | null> {
  const problem = coverFileProblem(file);
  if (problem === "type") throw new SpotThumbPickError(t("cover.err.type"));
  if (problem === "size") throw new SpotThumbPickError(t("cover.err.size"));
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch {
      throw new SpotThumbPickError(t("cover.err.read"));
    }
    if (!img.naturalWidth || !img.naturalHeight) throw new SpotThumbPickError(t("cover.err.read"));
    const crop = await cropDialog(img);
    return crop ? await encode(img, crop) : null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function cropDialog(img: HTMLImageElement): Promise<CoverCrop | null> {
  return new Promise((resolve) => {
    const overlay = el("div", "cover-crop spot-thumb-crop");
    const box = el("div", "cover-crop-box");
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", t("rt.spots.thumbCrop.title"));
    const viewport = el("div", "spot-thumb-crop-viewport");
    const stage = el("div", "spot-thumb-crop-stage");
    stage.tabIndex = 0;
    const photo = img.cloneNode() as HTMLImageElement;
    photo.className = "spot-thumb-crop-img";
    photo.alt = "";
    photo.draggable = false;
    stage.append(photo);
    const guide = el("div", "spot-thumb-crop-guide");
    guide.setAttribute("aria-hidden", "true");
    viewport.append(stage, guide);
    const zoomRow = el("label", "cover-crop-zoom");
    const zoomSlider = el("input") as HTMLInputElement;
    zoomSlider.type = "range";
    zoomSlider.min = "1";
    zoomSlider.max = "4";
    zoomSlider.step = "0.01";
    zoomSlider.value = "1";
    zoomRow.append(el("span", "", t("cover.crop.zoom")), zoomSlider);
    const foot = el("div", "cover-crop-foot");
    const cancel = button(t("cover.crop.cancel"), "rt-back", () => close(null));
    const use = button(t("cover.crop.use"), "rt-next", () => close(crop));
    foot.append(cancel, use);
    box.append(
      el("h2", "rt-q", t("rt.spots.thumbCrop.title")),
      el("p", "rt-sub", t("rt.spots.thumbCrop.hint", { size: String(SPOT_THUMB_OUTPUT_PX) })),
      viewport,
      zoomRow,
      foot,
    );
    overlay.append(box);
    document.body.append(overlay);

    const cropSide = () => {
      const side = viewport.clientWidth;
      return side > 0 ? side : 320;
    };

    let crop = spotThumbStartCrop(cropSide(), cropSide(), img.naturalWidth, img.naturalHeight);
    const paint = () => {
      const s = coverScale(crop);
      photo.style.width = `${img.naturalWidth}px`;
      photo.style.height = `${img.naturalHeight}px`;
      photo.style.transform = `translate(${crop.x}px, ${crop.y}px) scale(${s})`;
      zoomSlider.value = String(crop.zoom);
    };
    paint();

    const pointers = new Map<number, { x: number; y: number }>();
    let pinch: { dist: number; zoom: number } | null = null;
    const local = (e: { clientX: number; clientY: number }) => {
      const r = stage.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const spread = () => {
      const [a, b] = [...pointers.values()];
      return a && b ? { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } } : null;
    };
    stage.addEventListener("pointerdown", (e) => {
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, local(e));
      const s = spread();
      pinch = s ? { dist: s.dist, zoom: crop.zoom } : null;
      stage.classList.add("is-dragging");
    });
    stage.addEventListener("pointermove", (e) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const at = local(e);
      pointers.set(e.pointerId, at);
      const s = spread();
      if (pinch && s && pinch.dist > 0) {
        crop = spotThumbZoomCrop(crop, (pinch.zoom * s.dist) / pinch.dist, s.mid.x, s.mid.y);
      } else if (pointers.size === 1) {
        crop = spotThumbPanCrop(crop, at.x - prev.x, at.y - prev.y);
      }
      paint();
    });
    const lift = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      const s = spread();
      pinch = s ? { dist: s.dist, zoom: crop.zoom } : null;
      if (!pointers.size) stage.classList.remove("is-dragging");
    };
    stage.addEventListener("pointerup", lift);
    stage.addEventListener("pointercancel", lift);
    stage.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close(null);
      if (e.key === "ArrowLeft") {
        crop = spotThumbPanCrop(crop, KEY_PAN_PX, 0);
        paint();
      }
      if (e.key === "ArrowRight") {
        crop = spotThumbPanCrop(crop, -KEY_PAN_PX, 0);
        paint();
      }
      if (e.key === "ArrowUp") {
        crop = spotThumbPanCrop(crop, 0, KEY_PAN_PX);
        paint();
      }
      if (e.key === "ArrowDown") {
        crop = spotThumbPanCrop(crop, 0, -KEY_PAN_PX);
        paint();
      }
    });
    zoomSlider.addEventListener("input", () => {
      crop = spotThumbZoomCrop(crop, Number(zoomSlider.value));
      paint();
    });
    stage.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const at = local(e);
        crop = spotThumbZoomCrop(crop, crop.zoom * (1 - e.deltaY * WHEEL_ZOOM), at.x, at.y);
        paint();
      },
      { passive: false },
    );

    const close = (value: CoverCrop | null) => {
      overlay.remove();
      resolve(value);
    };
    requestAnimationFrame(() => {
      const side = cropSide();
      crop = spotThumbStartCrop(side, side, img.naturalWidth, img.naturalHeight);
      paint();
      stage.focus();
    });
  });
}

async function encode(img: HTMLImageElement, crop: CoverCrop): Promise<Blob> {
  const { sx, sy, sw, sh, width, height } = spotThumbCropOutput(crop);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new SpotThumbPickError(t("cover.err.read"));
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
  const webp = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/webp", COVER_WEBP_QUALITY));
  if (webp && webp.size <= COVER_MAX_UPLOAD_BYTES) return webp;
  const jpg = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/jpeg", COVER_WEBP_QUALITY));
  if (!jpg) throw new SpotThumbPickError(t("cover.err.read"));
  if (jpg.size > COVER_MAX_UPLOAD_BYTES) throw new SpotThumbPickError(t("cover.err.size"));
  return jpg;
}
